/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import { GenericCard } from "@ui-components/Card/GenericCard";
import { GenericTable } from "@ui-components/Table/GenericTable";


import { ReactComponent as IconEdit } from "../../../../shared/assets/Icons/IconEdit_KE.svg";
import { ReactComponent as IconCheck } from "../../../../shared/assets/Icons/IconCheckSquared.svg";
import { ReactComponent as IconCancel } from "../../../../shared/assets/Icons/IconCrossSquare.svg";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { useProcessingOverlay } from "@shared/hooks/useProcessingOverlay";
import { AxiosResponse } from "axios";
import { useSearchParams } from "react-router-dom";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import { mapConnectionDetailToSections, mapDatacenterDetailToSections } from "kontrolEdge/constants/constants";
import { getTextColor } from "kontrolEdge/helpers/getTextColor";
import { updateDatacenterNameTagAPI } from "../requests/request";
import { DatacenterDetailSection, SectionTable, SubnetRow } from "./DatacenterDetailsData";


interface Props {
	data: DatacenterDetailSection;
}

const DataCenterDetailsCard: React.FC<Props> = ({ data }) => {
	const api = useAxiosAuth0();
	const [nameValue, setNameValue] = useState("Data Center Name");
	const [isEditingName, setIsEditingName] = useState(false);
	const [originalName, setOriginalName] = useState("Data Center Name");
	const { tenantId } = useAppSelector(state => state.selection);

	const [tagValue, setTagValue] = useState("TAG");
	const [isEditingTag, setIsEditingTag] = useState(false);
	const [originalTag, setOriginalTag] = useState("TAG");

	const [isFetching, setIsFetching] = useState(false);
	const overlaySpinner = useProcessingOverlay(isFetching);
	//Port id selected from MainPort
	const [searchParams] = useSearchParams();
	const datacenterId = searchParams.get("datacenterId");
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const [rawConnectionData, setRawConnectionData] = useState<any>(null);
	const [connectionDetailsData, setConnectionDetailsData] = useState<DatacenterDetailSection[]>([]);
	const { performanceData } = useAppSelector(state => state.datacenter);
	const shouldCollapse = data.isCollapsible ?? true;

	// Handlers for Name and Tag editing
	const handleSaveNameTag = async () => {
		if (!datacenterId) return;
		try {
			setIsFetching(true);
			const response: AxiosResponse = await updateDatacenterNameTagAPI(api, datacenterId, tenantId, {
				name: nameValue,
				tag: tagValue,
			});
			setRawConnectionData(response.data.data);
			setOriginalName(nameValue);
			setOriginalTag(tagValue);
			setIsEditingName(false);
			setIsEditingTag(false);
		} catch (error) {
			console.error("Error updating port name and tag", error);
		} finally {
			setIsFetching(false);
		}
	};
	
	const handleCancelEditName = () => {
		setNameValue(originalName);
		setIsEditingName(false);
	};
	
	const handleCancelEditTag = () => {
		setTagValue(originalTag);
		setIsEditingTag(false);
	};

	// Update connectionDetailsData when performanceData changes (and rawConnectionData exists)
	useEffect(() => {
		if (rawConnectionData && performanceData) {
			setConnectionDetailsData(mapDatacenterDetailToSections(rawConnectionData, performanceData));
		}
		}, [rawConnectionData, performanceData]);

	useEffect(() => {
		// Find the incoming values
	const nameItem = data.items.find(i => i.label === "Data Center Name");
		if (nameItem) {
			const nv = String(nameItem.value);
			setNameValue(nv);
			setOriginalName(nv);
		}
	const tagItem = data.items.find(i => i.label === "TAG");
		if (tagItem) {
			const tv = Array.isArray(tagItem.value)
			? tagItem.value.join(", ")
			: String(tagItem.value);
			setTagValue(tv);
			setOriginalTag(tv);
		}
	}, [data.items]);

		return (
			<>
			{overlaySpinner}
			<GenericCard
				isCollapsible={shouldCollapse}
				headerContent={data.header}
				bodyContent={
					<div className="space-y-2">
						{data.items.map((item, idx) => {
							if (item.type === "subtitle") {
							return (
								<div key={idx} className="pt-3 pb-1">
								<h4 className="text-sm font-medium text-semantic-text-secondary">
									{item.label}
								</h4>
								</div>
							);
							}
							if (item.label === "Data Center Name") {
								return (
									<div key={idx}
									className="flex content-center justify-between w-full2 border-b">
										<h3 className="text-sm text-semantic-text-primary">{item.label}</h3>
										<div className="flex items-center space-x-2">
										{isEditingName ? (
										<>
											<button onClick={handleSaveNameTag}>
												<IconCheck className="w-5 h-5 text-semantic-text-secondary" />
											</button>
											<button onClick={handleCancelEditName}>
												<IconCancel className="w-5 h-5 text-semantic-text-secondary" />
											</button>
											<input
												type="text"
												value={nameValue}
												onChange={(e) => setNameValue(e.target.value)}
												className="border border-gray-300 rounded px-2 py-1 text-sm w-40"
											/>
										</>
									) : (
											<>
												<button
													onClick={() => setIsEditingName(true)}
													className="text-gray-500 hover:text-gray-700"
												>
													<IconEdit className="w-4 h-4" />
												</button>
												<GenericTag label={nameValue} variant="neutral" size="small" minWidth="100px" className="text-center" />
											</>
										)}
										</div>
									</div>
								)
							}
							if (item.label === "TAG") {
								return (
								<div key={idx}
								className="flex justify-between w-full2 border-b">
								<h3 className="text-sm text-semantic-text-primary">{item.label}</h3>
								<div className="flex items-center space-x-2">
									{isEditingTag ? (
										<>
											<button onClick={handleSaveNameTag}>
												<IconCheck className="w-5 h-5 text-semantic-text-secondary" />
											</button>
											<button onClick={handleCancelEditTag}>
												<IconCancel className="w-5 h-5 text-semantic-text-secondary" />
											</button>
											<input
												type="text"
												value={tagValue}
												onChange={(e) => setTagValue(e.target.value)}
												className="border border-gray-300 rounded px-2 py-1 text-sm w-40"
											/>
										</>
									) : (
										<>
											<button
												onClick={() => setIsEditingTag(true)}
												className="text-gray-500 hover:text-gray-700"
											>
												<IconEdit className="w-4 h-4" />
											</button>
											<GenericTag label={tagValue} variant="neutral" size="small" minWidth="100px" className="text-center"/>
										</>
									)}
									</div>
								</div>
								)
							}
							// Default: render a non-editable key-value row
							return (
								<div
									key={idx}
									className={`flex justify-between w-full py-1 ${
										idx !== data.items.length - 1 ? "border-b" : ""
									}`}
								>
									<h3 className="text-sm text-semantic-text-primary">
										{item.label}
									</h3>
									{item.type === "tag" ? (
										<GenericTag label={String(item.value)} variant="success" />
									) : (
										<p
											className={`text-sm ${getTextColor(
												item.label,
												String(item.value || "")
											)}`}
										>
											{item.value || "-"}
										</p>
									)}
								</div>
							);
						})}
						{data.table && (
						<GenericTable
							columns={(data.table as SectionTable<SubnetRow>).columns}
							data={(data.table as SectionTable<SubnetRow>).data}
							tableClassName="w-full"
							isFooterVisible={false}
						/>
						)}
					</div>
				}
			/>
			</>
		);
	}

export default DataCenterDetailsCard;
